import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { OAuthButtons } from './OAuthButtons';
import { VKIDWidget } from './VKIDWidget';
import { trackEvent, setAnalyticsUserFromToken } from '../../utils/analytics';

export const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oauthError = searchParams.get('error');
  const oauthErrors = t('auth.oauthErrors', { returnObjects: true }) as Record<string, string>;
  const translatedError = error ? t(error, { defaultValue: error }) : null;
  const displayError = translatedError || (oauthError ? (oauthErrors[oauthError] ?? t('auth.loginError')) : null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('login_submit', { method: 'password' });
    try {
      await login(email, password);
      setAnalyticsUserFromToken(localStorage.getItem('token'));
      trackEvent('login_success', { method: 'password' });
      navigate('/dashboard');
    } catch (err: any) {
      trackEvent('login_error', { method: 'password', reason: String(err?.message || err || 'unknown').slice(0, 64) });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <h2 className="auth-form__heading">{t('auth.signInHeading')}</h2>

      {displayError && (
        <div className="auth-form__msg auth-form__msg--error">{displayError}</div>
      )}

      <div className="auth-form__field">
        <label className="auth-form__label" htmlFor="email">{t('auth.emailLabel')}</label>
        <input
          className="auth-form__input"
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="user@example.com"
          autoComplete="email"
        />
      </div>

      <div className="auth-form__field">
        <label className="auth-form__label" htmlFor="password">{t('auth.passwordLabel')}</label>
        <input
          className="auth-form__input"
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="••••••••"
          autoComplete="current-password"
        />
      </div>

      <button
        type="submit"
        className="auth-form__submit"
        disabled={isLoading}
      >
        {isLoading ? t('auth.loading') : t('auth.enterBtn')}
      </button>

      <OAuthButtons />
      <VKIDWidget />
    </form>
  );
};
